
import { PlanTier } from '@flagsync/skyline';
import { ExternalLink, Info } from 'lucide-react';
import React, { createContext, useContext, useState } from 'react';

import { StyledLink } from '@/components/styled-link';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { pricing } from '@/pages/unauthenticated/pricing/components/constants';
import { BillingSessionForm } from '@/pages/authenticated/dashboard/settings/organization/billing/components/billing-session-form';

export interface IllegalTierActionMetadata {
  currentTier: PlanTier;
  nextTier: PlanTier;
  organizationId: number;
  isOwner: boolean;
}

interface DialogContextValue {
  isOpen: boolean;
  openDialog: (metadata: IllegalTierActionMetadata) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextValue | undefined>(undefined);

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [metadata, setMetadata] = useState<IllegalTierActionMetadata>();

  const openDialog = (metadata: IllegalTierActionMetadata) => {
    setIsOpen(true);
    setMetadata(metadata);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setMetadata(undefined);
  };

  const value: DialogContextValue = {
    isOpen,
    openDialog,
    closeDialog,
  };

  const currentTier = pricing.tiers.find((t) => {
    if (!metadata?.currentTier) return false;
    return t.lookupKeys.includes(metadata.currentTier);
  })?.name;

  const nextTier = pricing.tiers.find((t) => {
    if (!metadata?.currentTier) return false;
    return t.lookupKeys.includes(metadata.nextTier);
  })?.name;

  return (
    <DialogContext.Provider value={value}>
      {children}
      <AlertDialog open={isOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Upgrade to {nextTier} Plan</AlertDialogTitle>
            <AlertDialogDescription className="flex flex-col space-y-4">
              <span>
                Your current{' '}
                <span className="font-semibold">{currentTier}</span> plan does
                not support this action. To continue, you&apos;ll need to
                upgrade your plan.
              </span>
              {metadata?.isOwner ? (
                <span>
                  Check out our{' '}
                  <StyledLink
                    target="_blank"
                    href="/pricing"
                    className="inline-flex items-center gap-x-1"
                  >
                    pricing page <ExternalLink className="w-4 h-4" />
                  </StyledLink>{' '}
                  to see what&apos;s included in the {nextTier} plan.
                </span>
              ) : (
                <Alert>
                  <AlertDescription className="flex items-center pt-0 gap-x-2">
                    <Info className="w-4 h-4" />
                    Contact the owner of this organization to upgrade your plan.
                  </AlertDescription>
                </Alert>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeDialog}>
              {metadata?.isOwner ? 'No thanks' : 'Close'}
            </AlertDialogCancel>
            {metadata?.isOwner && (
              <BillingSessionForm
                sparkle
                organizationId={metadata?.organizationId}
                ctaText="Upgrade"
              />
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider>
  );
};

export const useUpgradeDialog = () => {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useUpgradeDialog must be used within a DialogProvider');
  }
  return context.openDialog;
};
